/* eslint-disable no-console */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {SampleExcel} from '../../constants/SampleExcel';
import {ISelect} from '../../interface/ISelect';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import supplierInfo from '../../services/modules/OM/supplierInfoDetails';
import productInfoAPI from '../../services/modules/PRODUCTS/ProductInfoAPI';
import rawMaterialAPI from '../../services/modules/PRODUCTS/rawMaterialAPI';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import usePlatformStore from '../../store/platformStore/platformStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {shouldBlockFree} from '../../utils/SubscriptionHelper';
import DisplayModal from '../DisplayModal/DisplayModal';
import RawMaterialDis from '../DisplayModal/ProductInfoDis/RawMaterialDis';
import DisposalDisplay from '../DisplayModal/WasteDisplay/DisposalDisplay';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {yearKeyLabel} from '../Forms/Constants/IP/commonKeyLabel';
import {FacilityKey} from '../Forms/Constants/IP/FacilityKeyLabel';
import {upstreamKey} from '../Forms/Constants/IP/UpstreamKeyLabel';
import {
  RawMaterialKey,
  RawMaterialLabel
} from '../Forms/Constants/Products/RawMaterialKeyAndLabel';
import RawAverageForm from '../Forms/RawAverageForm';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {FullInfo, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import {returnProductFilter} from './Utils/returnProductFilters';
import {sumOfTimeFrames} from './Utils/sumValue';
import TableTitleHeader from './Utils/TableTitleHeader';

const RawAverageTable: React.FC<{
  selectedYear: string;
  isAverage?: boolean;
}> = ({selectedYear, isAverage}) => {
  interface DataType {
    _id: string;
    fuelType: number;
    noOfUnits: boolean;
    deviceType: number;
    fuelUsed: boolean;
    method: number;
    impacts: boolean;
    owned: boolean;
    quantity: number;
    cost: number;
    comment: string;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [showModelInfo, setShowModelInfo] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [openDisplayModal, setOpenDisplayModal] = useState(false);
  const [disposalModalData, setDisposalModalData] = useState([]);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [formSuppliers, setFormSuppliers] = useState([]);
  const [isFormLoading, setIsFormLoading] = useState(false);

  const [openDisplayModalType, setOpenDisplayModalType] = useState({
    title: '',
    dataKey: ''
  });
  const [formSelectedYear, setFormSelectedYear] = useState('');

  const companyId = useCompanyStore.getState().companyId;

  const {products, rawMaterials} = useJSONStore.getState();

  const [rawMaterialFilter, setRawMaterialFilter] = useState<any>({});
  const [formRawMaterialFilter, setFormRawMaterialFilter] = useState<any>({});

  const facilityDetails = useMemo(() => {
    return (
      useCompanyStore
        .getState()
        .facilityDetails?.[selectedYear ?? '']?.map((item: any) => {
          return {value: item._id, label: item?.[FacilityKey.FACILITY_NAME]};
        }) ?? []
    );
  }, [selectedYear]);

  const method = isAverage ? 'Average' : 'Supplier';

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      rawMaterialAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${companyId}/${selectedYear}/${method}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  useEffect(() => {
    setIsRefresh(true);
    Promise.all([])
      .catch(() => {})
      .finally(() => {
        setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (selectedYear && allJSON) {
      setIsRefresh(true);
      Promise.all([
        rawMaterialAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}/${method}`
        ),
        productInfoAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}`
        ),
        !isAverage &&
          supplierInfo(
            APITypeEnum.GET_ALL,
            'company',
            `${companyId}/${selectedYear}/upstream`
          )
      ])
        .then(([res1, res2, res3]) => {
          setDataSource(res1 ?? []);
          setSuppliers(
            res3.length > 0
              ? res3.map((item: any) => {
                  return {
                    label: item?.[upstreamKey.SUPPLIER_NAME],
                    value: item._id,
                    data: item?.data
                  };
                })
              : []
          );

          setRawMaterialFilter(
            returnProductFilter(
              [
                {
                  mainKey: RawMaterialKey.PRODUCT_ID,
                  filterer: products,
                  subKey: '',
                  returnFilterKey: 'productFilter'
                },
                {
                  mainKey: RawMaterialKey.FACILITY_MAIN,
                  filterer: facilityDetails,
                  subKey: RawMaterialKey.FACILITY_ID,
                  returnFilterKey: 'facilityFilter'
                },
                {
                  mainKey: RawMaterialKey.RAW_MATERIAL_MAIN,
                  filterer: rawMaterials,
                  subKey: RawMaterialKey.RAW_MATERIAL_ID,
                  returnFilterKey: 'rawFilter'
                }
              ],
              res2
            )
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [selectedYear, allJSON]);

  useEffect(() => {
    if (formSelectedYear) {
      Promise.all([
        productInfoAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${formSelectedYear}`
        ),
        !isAverage &&
          supplierInfo(
            APITypeEnum.GET_ALL,
            'company',
            `${companyId}/${formSelectedYear}/upstream`
          )
      ])
        .then(([res2, res3]) => {
          const facilityDetail =
            useCompanyStore
              .getState()
              .facilityDetails?.[formSelectedYear ?? '']?.map((item: any) => {
                return {
                  value: item._id,
                  label: item?.[FacilityKey.FACILITY_NAME]
                };
              }) ?? [];
          setFormSuppliers(
            res3.length > 0
              ? res3.map((item: any) => {
                  return {
                    label: item?.[upstreamKey.SUPPLIER_NAME],
                    value: item._id,
                    data: item?.data
                  };
                })
              : []
          );
          setFormRawMaterialFilter(
            returnProductFilter(
              [
                {
                  mainKey: RawMaterialKey.PRODUCT_ID,
                  filterer: products,
                  subKey: '',
                  returnFilterKey: 'productFilter'
                },
                {
                  mainKey: RawMaterialKey.FACILITY_MAIN,
                  filterer: facilityDetail,
                  subKey: RawMaterialKey.FACILITY_ID,
                  returnFilterKey: 'facilityFilter'
                },
                {
                  mainKey: RawMaterialKey.RAW_MATERIAL_MAIN,
                  filterer: rawMaterials,
                  subKey: RawMaterialKey.RAW_MATERIAL_ID,
                  returnFilterKey: 'rawFilter'
                }
              ],
              res2
            )
          );
        })
        .catch(() => {})
        .finally(() => {
          setIsFormLoading(false);
        });
    }
  }, [formSelectedYear]);

  const handleDelete = (_id: string[] | string) => {
    rawMaterialAPI(APITypeEnum.DELETE_ALL, {
      rawMaterial: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);
  const {productFilter, facilityFilter, rawFilter} = rawMaterialFilter;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const rawKeyLabel = useMemo(() => {
    return [
      {
        key: yearKeyLabel.key,
        label: yearKeyLabel.label,
        type: objTypes.string
      },
      {
        key: RawMaterialKey.PRODUCT_ID,
        label: RawMaterialLabel?.PRODUCT_ID,
        type: objTypes.json,
        format: formSelectedYear
          ? formRawMaterialFilter?.productFilter
          : rawMaterialFilter?.productFilter
      },
      {
        key: RawMaterialKey.FACILITY,
        label: RawMaterialLabel.FACILITY,
        type: objTypes.json,
        format: formSelectedYear
          ? formRawMaterialFilter?.facilityFilter
          : rawMaterialFilter?.facilityFilter
      },
      {
        key: RawMaterialKey.RAW_MATERIAL,
        label: RawMaterialLabel.RAW_MATERIAL,
        type: objTypes.json,
        format: formSelectedYear
          ? formRawMaterialFilter?.rawFilter
          : rawMaterialFilter?.rawFilter
      },
      {
        key: RawMaterialKey.ADD_SUPPLIER,
        label: RawMaterialLabel.ADD_SUPPLIER,
        type: objTypes.supplierData,
        format: formSelectedYear ? formSuppliers : suppliers,
        isGreen: true,
        renderType: 'multi',
        hidden: isAverage
      },
      {
        key: RawMaterialKey.RECYCLE_DATA,
        mainLabel: [
          RawMaterialLabel.RECYCLE.recycle,
          RawMaterialLabel.RECYCLE.reuse,
          RawMaterialLabel.RECYCLE.fresh
        ],
        label: [RawMaterialLabel.WEIGHT_TF, RawMaterialLabel.COST_TF],
        tfKey: [RawMaterialKey.WEIGHT_TF, RawMaterialKey.COST_TF],
        type: objTypes.timeFrameData,
        renderType: 'multi'
      },
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              key: checkerStatusKey.STATUS,
              label: checkerStatusLabel.STATUS,
              type: objTypes.status
            },
            {
              key: checkerStatusKey.COMMENT,
              label: checkerStatusLabel.COMMENT,
              type: objTypes.comment
            }
          ]
        : [])
    ];
  }, [formRawMaterialFilter, formSuppliers, rawMaterialFilter, suppliers]);

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = useMemo(() => {
    return isAverage
      ? [
          {
            title: RawMaterialLabel.RAW_MATERIAL,
            dataIndex: RawMaterialKey.RAW_MATERIAL,
            editable: true,
            type: 'select',
            ...SearchFilter(
              RawMaterialLabel.RAW_MATERIAL,
              RawMaterialKey.RAW_MATERIAL,
              rawFilter
            ),
            render: (value) => {
              return (
                <>
                  {
                    rawFilter?.find((item: any) => {
                      return item.value === value;
                    })?.label
                  }
                </>
              );
            }
          },
          {
            title: RawMaterialLabel.PRODUCT_ID,
            dataIndex: RawMaterialKey.PRODUCT_ID,
            editable: true,
            type: 'select',
            ...SearchFilter(
              RawMaterialLabel.PRODUCT_ID,
              RawMaterialKey.PRODUCT_ID,
              productFilter
            ),
            render: (value: any) => {
              return (
                <>
                  {
                    productFilter?.find((item: any) => {
                      return item.value === value;
                    })?.label
                  }
                </>
              );
            }
          },
          {
            title: RawMaterialLabel.FACILITY,
            dataIndex: RawMaterialKey.FACILITY,
            editable: true,
            type: 'select',

            render: (value) => {
              return (
                <>
                  {
                    facilityFilter?.find((item: any) => {
                      return item.value === value;
                    })?.label
                  }
                </>
              );
            }
          },
          {
            title: RawMaterialLabel.WEIGHT_TF,
            dataIndex: RawMaterialKey.RECYCLE_DATA,
            editable: true,
            type: 'select',
            render: (value: any) => {
              const weightUnit = value?.[0]?.timeFrameData?.find(
                (item: any) => item?.type === RawMaterialKey.WEIGHT_TF
              )?.unit;

              return (
                <div
                  onClick={() => {
                    if (shouldBlockFree()) {
                      usePlatformStore
                        .getState()
                        .setOpenOnFreeTrialMessageModel(true);

                      return;
                    }
                    setOpenDisplayModal(true);
                    setDisposalModalData(value);
                    setOpenDisplayModalType({
                      title: `${RawMaterialLabel.WEIGHT_TF} (${weightUnit})`,
                      dataKey: RawMaterialKey.WEIGHT_TF
                    });
                  }}
                  style={{
                    textDecorationLine: 'underline',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                  <div>{`${sumOfTimeFrames(
                    value,
                    RawMaterialKey.WEIGHT_TF
                  )}`}</div>
                </div>
              );
            }
          },
          {
            title: RawMaterialLabel.COST_TF,
            dataIndex: RawMaterialKey.RECYCLE_DATA,
            editable: true,
            type: 'select',
            render: (value: any) => {
              const costUnit = value?.[0]?.timeFrameData?.find(
                (item: any) => item?.type === RawMaterialKey.COST_TF
              )?.unit;

              return (
                <div
                  onClick={() => {
                    if (shouldBlockFree()) {
                      usePlatformStore
                        .getState()
                        .setOpenOnFreeTrialMessageModel(true);

                      return;
                    }
                    setOpenDisplayModal(true);
                    setDisposalModalData(value);
                    setOpenDisplayModalType({
                      title: `${RawMaterialLabel.COST_TF} (${costUnit})`,
                      dataKey: RawMaterialKey.COST_TF
                    });
                  }}
                  style={{
                    textDecorationLine: 'underline',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                  <div>{`${sumOfTimeFrames(
                    value,
                    RawMaterialKey.COST_TF,
                    true
                  )}`}</div>
                </div>
              );
            }
          },
          ...(hasUserRole(UserRole.Maker_Role) ||
          hasUserRole(UserRole.Both_role) ||
          hasViewRole(UserRole.View_Only)
            ? [
                {
                  title: RawMaterialLabel.STATUS_COMMENTS,
                  dataIndex: 'status',
                  editable: true,
                  type: 'select',
                  render: (value: any, record: any) => {
                    const colorText = getStatusColor(value);

                    return (
                      <div
                        style={{
                          fontWeight: 700,
                          color: colorText.color,
                          cursor: 'pointer'
                        }}
                      >
                        {colorText.text}
                        {value === 'rejected' && record?.comment && (
                          <span className="customComment">
                            - {record.comment}
                          </span>
                        )}
                      </div>
                    );
                  }
                }
              ]
            : []),
          // ...(hasUserRole(UserRole.Checker_Role) ||
          // hasUserRole(UserRole.Both_role)
          //   ? [
          //       {
          //         title: checkerStatusLabel.DATA_SOURCE,
          //         dataIndex: checkerStatusKey.DATA_SOURCE,
          //         editable: true,
          //         type: 'select',
          //         render: (value: any) => {
          //           return (
          //             <div>
          //               {value === checkerStatusKey.MANUAL
          //                 ? checkerStatusLabel.MANUAL
          //                 : checkerStatusLabel.BULK_UPLOAD}
          //             </div>
          //           );
          //         }
          //       }
          //     ]
          //   : []),
          ...(hasUserRole(UserRole.Checker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: RawMaterialLabel.STATUS,
                  dataIndex: 'status',
                  render: (value: any, record: any) => {
                    const colorText = getStatusColor(value);

                    return (
                      <Tag
                        onClick={() => {
                          if (shouldBlockFree()) {
                            usePlatformStore
                              .getState()
                              .setOpenOnFreeTrialMessageModel(true);

                            return;
                          }
                          setShowModelInfo(true);
                          setEditData(record);
                          setIsEdit(true);
                        }}
                        color={colorText.color}
                        style={{
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        {colorText.text}
                      </Tag>
                    );
                  }
                }
              ]
            : []),
          ...(hasUserRole(UserRole.Checker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: FullInfo.title,
                  dataIndex: 'fullInfo',
                  editable: true,
                  type: 'select',
                  render: (_: any, record: any) => {
                    return (
                      <Button
                        onClick={() => {
                          if (shouldBlockFree()) {
                            usePlatformStore
                              .getState()
                              .setOpenOnFreeTrialMessageModel(true);

                            return;
                          }
                          setShowModelInfo(true);
                          setEditData(record);
                          setIsEdit(true);
                        }}
                        shape="circle"
                        style={{
                          marginRight: 16,
                          cursor: 'pointer',
                          color: '#435B37',
                          background: '#F5FBF3',
                          border: 0
                        }}
                      >
                        <ExclamationOutlined />
                      </Button>
                    );
                  }
                }
              ]
            : []),
          ...(hasUserRole(UserRole.Maker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: Title.actionTitle,
                  dataIndex: 'operation',
                  type: '' as const,
                  fixed: 'right' as const,
                  width: '200px',
                  render: (value: any, record: any) =>
                    dataSource.length >= 1 ? (
                      <ActionsButton
                        record={record}
                        setShowModel={setShowModel}
                        setEditData={setEditData}
                        setIsEdit={setIsEdit}
                        handleDelete={handleDelete}
                      />
                    ) : null
                }
              ]
            : [])
        ]
      : [
          {
            title: RawMaterialLabel.RAW_MATERIAL,
            dataIndex: RawMaterialKey.RAW_MATERIAL,
            editable: true,
            type: 'select',
            ...SearchFilter(
              RawMaterialLabel.RAW_MATERIAL,
              RawMaterialKey.RAW_MATERIAL,
              rawFilter
            ),
            render: (value) => {
              return (
                <>
                  {
                    rawFilter?.find((item: any) => {
                      return item.value === value;
                    })?.label
                  }
                </>
              );
            }
          },
          {
            title: RawMaterialLabel.PRODUCT_ID,
            dataIndex: RawMaterialKey.PRODUCT_ID,
            editable: true,
            type: 'select',
            ...SearchFilter(
              RawMaterialLabel.PRODUCT_ID,
              RawMaterialKey.PRODUCT_ID,
              productFilter
            ),
            render: (value) => {
              return (
                <>
                  {
                    productFilter?.find((item: any) => {
                      return item.value === value;
                    })?.label
                  }
                </>
              );
            }
          },
          {
            title: RawMaterialLabel.FACILITY,
            dataIndex: RawMaterialKey.FACILITY,
            editable: true,
            type: 'select',

            render: (value) => {
              return (
                <>
                  {
                    facilityFilter?.find((item: any) => {
                      return item.value === value;
                    })?.label
                  }
                </>
              );
            }
          },

          {
            title: RawMaterialLabel.WEIGHT_TF,
            dataIndex: RawMaterialKey.RECYCLE_DATA,
            editable: true,
            type: 'select',
            render: (value) => {
              const weightUnit = value?.[0]?.timeFrameData?.find(
                (item: any) => item?.type === RawMaterialKey.WEIGHT_TF
              )?.unit;

              return (
                <div
                  onClick={() => {
                    if (shouldBlockFree()) {
                      usePlatformStore
                        .getState()
                        .setOpenOnFreeTrialMessageModel(true);

                      return;
                    }
                    setOpenDisplayModal(true);
                    setDisposalModalData(value);
                    setOpenDisplayModalType({
                      title: `${RawMaterialLabel.WEIGHT_TF} (${weightUnit})`,
                      dataKey: RawMaterialKey.WEIGHT_TF
                    });
                  }}
                  style={{
                    textDecorationLine: 'underline',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                  <div>{`${sumOfTimeFrames(
                    value,
                    RawMaterialKey.WEIGHT_TF
                  )}`}</div>
                </div>
              );
            }
          },
          {
            title: RawMaterialLabel.COST_TF,
            dataIndex: RawMaterialKey.RECYCLE_DATA,
            editable: true,
            type: 'select',
            render: (value: any) => {
              const costUnit = value?.[0]?.timeFrameData?.find(
                (item: any) => item?.type === RawMaterialKey.COST_TF
              )?.unit;

              return (
                <div
                  onClick={() => {
                    if (shouldBlockFree()) {
                      usePlatformStore
                        .getState()
                        .setOpenOnFreeTrialMessageModel(true);

                      return;
                    }
                    setOpenDisplayModal(true);
                    setDisposalModalData(value);
                    setOpenDisplayModalType({
                      title: `${RawMaterialLabel.COST_TF} (${costUnit})`,
                      dataKey: RawMaterialKey.COST_TF
                    });
                  }}
                  style={{
                    textDecorationLine: 'underline',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                  <div>{`${sumOfTimeFrames(
                    value,
                    RawMaterialKey.COST_TF,
                    true
                  )}`}</div>
                </div>
              );
            }
          },
          {
            title: RawMaterialLabel.ADD_SUPPLIER,
            dataIndex: RawMaterialKey.ADD_SUPPLIER,
            editable: true,
            render: (value) => {
              return (
                <div
                  onClick={() => {
                    if (shouldBlockFree()) {
                      usePlatformStore
                        .getState()
                        .setOpenOnFreeTrialMessageModel(true);

                      return;
                    }
                    setOpenDisplayModal(true);
                    setDisposalModalData(value);
                    setOpenDisplayModalType({
                      title: RawMaterialLabel.ADD_SUPPLIER,
                      dataKey: RawMaterialKey.ADD_SUPPLIER
                    });
                  }}
                  style={{
                    textDecorationLine: 'underline',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                  <div>{value?.length}</div>
                </div>
              );
            }
          },
          ...(hasUserRole(UserRole.Maker_Role) ||
          hasUserRole(UserRole.Both_role) ||
          hasViewRole(UserRole.View_Only)
            ? [
                {
                  title: RawMaterialLabel.STATUS_COMMENTS,
                  dataIndex: 'status',
                  editable: true,
                  type: 'select',
                  render: (value: any, record: any) => {
                    const colorText = getStatusColor(value);

                    return (
                      <div
                        style={{
                          fontWeight: 700,
                          color: colorText.color,
                          cursor: 'pointer'
                        }}
                      >
                        {colorText.text}
                        {value === 'rejected' && record?.comment && (
                          <span className="customComment">
                            - {record.comment}
                          </span>
                        )}
                      </div>
                    );
                  }
                }
              ]
            : []),
          ...(hasUserRole(UserRole.Checker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: RawMaterialLabel.STATUS,
                  dataIndex: 'status',
                  render: (value: any, record: any) => {
                    const colorText = getStatusColor(value);

                    return (
                      <Tag
                        onClick={() => {
                          if (shouldBlockFree()) {
                            usePlatformStore
                              .getState()
                              .setOpenOnFreeTrialMessageModel(true);

                            return;
                          }
                          setShowModelInfo(true);
                          setEditData(record);
                          setIsEdit(true);
                        }}
                        color={colorText.color}
                        style={{
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        {colorText.text}
                      </Tag>
                    );
                  }
                }
              ]
            : []),
          ...(hasUserRole(UserRole.Checker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: FullInfo.title,
                  dataIndex: 'fullInfo',
                  editable: true,
                  type: 'select',
                  render: (_: any, record: any) => {
                    return (
                      <Button
                        onClick={() => {
                          if (shouldBlockFree()) {
                            usePlatformStore
                              .getState()
                              .setOpenOnFreeTrialMessageModel(true);

                            return;
                          }
                          setShowModelInfo(true);
                          setEditData(record);
                          setIsEdit(true);
                        }}
                        shape="circle"
                        style={{
                          marginRight: 16,
                          cursor: 'pointer',
                          color: '#435B37',
                          background: '#F5FBF3',
                          border: 0
                        }}
                      >
                        <ExclamationOutlined />
                      </Button>
                    );
                  }
                }
              ]
            : []),
          ...(hasUserRole(UserRole.Maker_Role) ||
          hasUserRole(UserRole.Both_role)
            ? [
                {
                  title: Title.actionTitle,
                  dataIndex: 'operation',
                  type: '' as const,
                  fixed: 'right' as const,
                  width: '200px',
                  render: (value: any, record: any) =>
                    dataSource.length >= 1 ? (
                      <ActionsButton
                        record={record}
                        setShowModel={setShowModel}
                        setEditData={setEditData}
                        setIsEdit={setIsEdit}
                        handleDelete={handleDelete}
                      />
                    ) : null
                }
              ]
            : [])
        ];
  }, [dataSource]);

  return (
    <div>
      <TableTitleHeader
        title="Raw Material Info"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Raw Material'
        }}
        sampleDownload={{href: SampleExcel.waste}}
        jobs={{setShowJobs, uploadType: uploadTypes.Waste}}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        showAddOnly={true}
        onlyYear={true}
        shouldEdit={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        }
      />
      <JobsModal
        open={showJobs}
        onCancel={() => {
          setShowJobs(false);
          setRefresh(true);
        }}
      />
      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? {
                type: 'checkbox',
                getCheckboxProps: (record: any) => ({
                  disabled: record.status === statusEm.ACCEPTED
                }),
                selectedRowKeys,
                onChange: (
                  selectedKey: React.Key[],
                  selectedRow: DataType[]
                ) => {
                  setSelectedRowKeys(
                    selectedRow.map((item) => {
                      return item._id;
                    })
                  );
                }
              }
            : undefined
        }
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />
      <DrawerForm
        open={showModel}
        API={rawMaterialAPI}
        data={isEdit ? editData : undefined}
        FormComponent={
          <RawAverageForm
            data={isEdit ? editData : undefined}
            setSubmitDisabled={setSubmitDisabled}
            isAverage={isAverage}
            rawMaterialFilter={
              formSelectedYear ? formRawMaterialFilter : rawMaterialFilter
            }
            suppliers={formSelectedYear ? formSuppliers : suppliers}
            setFormSelectedYear={setFormSelectedYear}
            setIsFormLoading={setIsFormLoading}
            isFormLoading={isFormLoading}
          />
        }
        onCancel={() => {
          setShowModel(false);
          setFormSelectedYear('');
        }}
        setRefresh={setRefresh}
        idKey={`${companyId}/${selectedYear}`}
        type={{key: 'method', value: method}}
        table={true}
        buttonDisabled={{value: submitDisabled, setter: setSubmitDisabled}}
        onlyYear={true}
        stepsTitle={['Raw Material Details', 'Quantity & Cost Details']}
        formPreview={rawKeyLabel}
      />
      <DisplayModal
        title={openDisplayModalType.title}
        open={openDisplayModal}
        onCancel={() => {
          setOpenDisplayModal(false);
        }}
        displayComponent={
          openDisplayModalType.title === RawMaterialLabel.ADD_SUPPLIER ? (
            <RawMaterialDis
              dataSource={disposalModalData}
              suppliers={suppliers}
            />
          ) : (
            <DisposalDisplay
              disposalModalData={disposalModalData}
              disposalModalType={openDisplayModalType.dataKey}
              disposalKey={RawMaterialKey.RECYCLE_METHOD}
            />
          )
        }
        isTable={openDisplayModalType.title === RawMaterialLabel.ADD_SUPPLIER}
      />
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        API={rawMaterialAPI}
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={rawKeyLabel} />
        }
        table={true}
        stepsTitle={['All Data']}
        isChecker={true}
        isAction={false}
      />
      {/* <StatusDrawer
        open={showStatusModal}
        onCancel={() => {
          setShowStatusModal(false);
        }}
        buttonText="Save"
        setRefresh={setRefresh}
        API={rawMaterialAPI}
        data={isEdit ? editData : undefined}
        FormComponent={<CheckerStatusForm data={selectedRowData} />}
        table={true}
        stepsTitle={['Status & Comments']}
      /> */}
      {/* <DrawerForm
        open={showStatusModal}
        onCancel={() => {
          setShowStatusModal(false);
        }}
        setRefresh={setRefresh}
        API={rawMaterialAPI}
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={selectedRowData} formPreview={rawKeyLabel} />
        }
        table={true}
        stepsTitle={['Status & Comments']}
        isChecker={true}
      /> */}
    </div>
  );
};

export default RawAverageTable;
